import type { AdminOrderListItemDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { OrdersFilterBar } from './_components/OrdersFilterBar';
import { buildOrderColumns } from './_lib/orderColumns';
import { hasActiveFilters, orderFiltersSchema } from './_lib/orderFilters';
import { OrderDetailDrawer } from './OrderDetailDrawer';
import { useAdminOrdersList } from '../../api/admin-orders';
import { RequirePermission } from '../../auth';
import { AdminDataGrid } from '../../components/data-grid';
import { useCursorPagination } from '../../hooks/useCursorPagination';
import { useUrlFilters } from '../../hooks/useUrlFilters';

/**
 * `/orders` — orders moderation list (AUT-123 / ADMIN-11).
 *
 * Gated whole-page on `manage_orders` (the controller-level permission).
 * Identity columns (customer / inspector name) render via
 * `<RedactedField perm="view_pii">`, which agrees with the server's
 * `piiRevealed` flag (both derive from the same backend permission).
 *
 * Filters + pagination are URL-state-driven via `useUrlFilters`; cursor
 * pagination bridges the cursor backend to the page-based DataGrid via
 * the shared `useCursorPagination`. Row click opens the 720px detail
 * drawer. Near-clone of the AUT-124 `/users` page.
 */
const OrdersListPageInner = (): ReactElement => {
  const { t } = useTranslation();
  const { filters, setFilters, reset } = useUrlFilters(orderFiltersSchema);
  const pager = useCursorPagination();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Any filter change resets pagination to the first page (the cursor
  // space is only valid for the filter set that produced it).
  const filterKey = `${(filters.status ?? []).join(',')}|${filters.q ?? ''}|${filters.from ?? ''}|${filters.to ?? ''}`;
  useEffect(() => {
    pager.reset();
    // `pager.reset` is stable (useCallback); re-run only on filter change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const query = useAdminOrdersList({
    ...(filters.status && filters.status.length > 0 ? { status: filters.status } : {}),
    ...(filters.q ? { q: filters.q } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    cursor: pager.cursor,
    limit: pager.pageSize,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const nextCursor = query.data?.nextCursor ?? null;

  // Feed the latest nextCursor into the pager once each fetch settles.
  useEffect(() => {
    if (query.isSuccess) pager.onResult(nextCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isSuccess, query.dataUpdatedAt, nextCursor]);

  const columns = useMemo(() => buildOrderColumns(t), [t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
          {t('orders.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('orders.subtitle')}
        </Typography>
      </Box>

      <OrdersFilterBar
        filters={filters}
        setFilters={setFilters}
        canClear={hasActiveFilters(filters)}
        onClear={reset}
      />

      <AdminDataGrid<AdminOrderListItemDto>
        rows={items}
        columns={columns}
        rowCount={pager.rowCount(items.length)}
        loading={query.isLoading || query.isFetching}
        error={query.error}
        paginationModel={pager.paginationModel}
        onPaginationModelChange={pager.onPaginationModelChange}
        sortModel={[]}
        onSortModelChange={() => undefined}
        onRowClick={(row) => setSelectedId(row.id)}
        emptyState={
          <Typography variant="body2" color="text.secondary">
            {t('orders.empty')}
          </Typography>
        }
      />

      <OrderDetailDrawer orderId={selectedId} onClose={() => setSelectedId(null)} />
    </Box>
  );
};

const Observed = observer(OrdersListPageInner);

/** Whole-page gate: no `manage_orders` → the default 403 panel. */
export const OrdersListPage = (): ReactElement => (
  <RequirePermission perm="manage_orders">
    <Observed />
  </RequirePermission>
);
